import { useCallback, useState } from 'react'
import { assertCycleDay } from '@domain/finance'
import type { Account, CreditCard } from '@domain/finance'
import type { NewCreditCard, ProviderAccount } from '@domain/ports/finance-repository'
import { centsToInput, parseAmountInput } from '@shared/money/parse'
import { formatBRL } from '@shared/money/format'
import { useFinanceRepositories } from '@ui/finance/finance-context'
import { Problem, ProviderLinkField, SelectField, TextField } from './CrudeFields'
import { CARD_BRANDS, STATUSES, optionsOf } from './labels'
import { useCollection } from './use-collection'

/**
 * CRUD de cartões, cru. Mesma forma da tela de contas, com o que dá ao cartão a
 * razão de ser uma entidade à parte: `closingDay` e `dueDay`.
 *
 * **Os dois dias são conferidos aqui com a função do domínio.** `assertCycleDay`
 * é a mesma que `invoiceCycleFor` usa e a mesma que o adapter chama — repetir a
 * faixa 1..31 numa terceira redação daria um lugar a mais para errá-la. Aqui
 * ela vale como resposta **antecipada**: um cartão com `closingDay: 40` que
 * chegasse ao banco não daria erro visível, só produziria fatura nenhuma meses
 * depois, longe do formulário que o criou.
 *
 * A conta de pagamento é uma **dica** de conciliação (`Docs/02-Dominio/Invoice.md`),
 * não verdade — daí "nenhuma" ser uma opção legítima do seletor.
 */

const ID = 'cartoes'
const NO_ACCOUNT = ''

type Draft = {
  name: string
  institution: string
  brand: CreditCard['brand']
  lastFourDigits: string
  creditLimit: string
  closingDay: string
  dueDay: string
  paymentAccountId: string
}

const EMPTY: Draft = {
  name: '',
  institution: '',
  brand: 'mastercard',
  lastFourDigits: '',
  creditLimit: '',
  closingDay: '',
  dueDay: '',
  paymentAccountId: NO_ACCOUNT,
}

function draftOf(card: CreditCard): Draft {
  return {
    name: card.name,
    institution: card.institution,
    brand: card.brand,
    lastFourDigits: card.lastFourDigits ?? '',
    creditLimit: card.creditLimitCents === null ? '' : centsToInput(card.creditLimitCents),
    closingDay: String(card.closingDay),
    dueDay: String(card.dueDay),
    paymentAccountId: card.paymentAccountId ?? NO_ACCOUNT,
  }
}

/**
 * O dia do ciclo, ou a razão de recusa em português. `Number(' ')` é `0` e
 * `Number('')` também, então a conversão passa por `Number.isInteger` dentro de
 * `assertCycleDay` — que é justamente o ponto de delegar em vez de reescrever.
 */
function cycleDay(text: string, field: 'closingDay' | 'dueDay'): number | string {
  const trimmed = text.trim()
  if (trimmed === '') return 'Informe o dia (de 1 a 31).'
  try {
    const day = Number(trimmed)
    assertCycleDay(day, field)
    return day
  } catch (error: unknown) {
    return error instanceof Error ? error.message : 'Dia inválido.'
  }
}

export function CardsScreen() {
  const { creditCardRepository, accountRepository, providerAccountRepository } =
    useFinanceRepositories()
  const loadCards = useCallback(() => creditCardRepository.list(), [creditCardRepository])
  const loadAccounts = useCallback(() => accountRepository.list(), [accountRepository])
  const { state, reload } = useCollection<CreditCard>(loadCards)
  const accounts = useCollection<Account>(loadAccounts)
  const loadProvider = useCallback(
    () => providerAccountRepository.list(),
    [providerAccountRepository],
  )
  const catalogo = useCollection<ProviderAccount>(loadProvider)

  const [editingId, setEditingId] = useState<string | null>(null)
  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [failure, setFailure] = useState<string | null>(null)

  const parsedLimit = parseAmountInput(draft.creditLimit)
  const limitProblem = parsedLimit.status === 'invalid' ? parsedLimit.message : null
  const closing = cycleDay(draft.closingDay, 'closingDay')
  const due = cycleDay(draft.dueDay, 'dueDay')

  function change(patch: Partial<Draft>) {
    setDraft((current) => ({ ...current, ...patch }))
    setFailure(null)
  }

  function reset() {
    setEditingId(null)
    setDraft(EMPTY)
    setFailure(null)
  }

  async function submit() {
    setFailure(null)
    if (limitProblem !== null || typeof closing === 'string' || typeof due === 'string') return

    const shared = {
      name: draft.name,
      institution: draft.institution,
      brand: draft.brand,
      lastFourDigits: draft.lastFourDigits.trim() === '' ? null : draft.lastFourDigits.trim(),
      creditLimitCents: parsedLimit.status === 'ok' ? parsedLimit.cents : null,
      closingDay: closing,
      dueDay: due,
      paymentAccountId: draft.paymentAccountId === NO_ACCOUNT ? null : draft.paymentAccountId,
    }

    try {
      if (editingId === null) {
        const input: NewCreditCard = shared
        await creditCardRepository.create(input)
      } else {
        await creditCardRepository.update(editingId, shared)
      }
      reset()
      reload()
    } catch (error: unknown) {
      console.error('CardsScreen: a escrita falhou', error)
      setFailure(error instanceof Error ? error.message : 'Não foi possível salvar o cartão.')
    }
  }

  /**
   * O vínculo é gravado na hora — mesmo motivo do `AccountsScreen`: não se
   * digita, se escolhe de uma lista que o sync publicou, e ele é campo só do
   * patch.
   *
   * **A tela de cartão só oferece `kind: 'creditCard'`**, e isso não é
   * cosmético: vincular um cartão a uma conta bancária inverteria o sinal de
   * todas as transações dele (decisão 124a), e toda compra viraria receita.
   */
  async function vincular(card: CreditCard, externalId: string | null) {
    setFailure(null)
    try {
      await creditCardRepository.update(card.id, { providerAccountId: externalId })
      reload()
    } catch (error: unknown) {
      console.error('CardsScreen: o vínculo falhou', error)
      setFailure(error instanceof Error ? error.message : 'Não foi possível vincular o cartão.')
    }
  }

  async function setStatus(card: CreditCard, status: CreditCard['status']) {
    setFailure(null)
    try {
      await creditCardRepository.update(card.id, { status })
      reload()
    } catch (error: unknown) {
      console.error('CardsScreen: a mudança de status falhou', error)
      setFailure(error instanceof Error ? error.message : 'Não foi possível arquivar o cartão.')
    }
  }

  const accountOptions = [
    { value: NO_ACCOUNT, label: 'Nenhuma' },
    ...(accounts.state.status === 'loaded'
      ? accounts.state.items.map((account) => ({ value: account.id, label: account.name }))
      : []),
  ]

  return (
    <section aria-label="Cartões">
      <h2 className="mb-2 text-sm font-semibold tracking-tight">Cartões</h2>

      {state.status === 'loading' && (
        <p role="status" className="text-sm text-[var(--text-muted)]">
          Carregando os cartões…
        </p>
      )}
      {state.status === 'failed' && (
        <p role="alert" className="text-sm text-[var(--text-muted)]">
          Não foi possível carregar os cartões agora.
        </p>
      )}

      {state.status === 'loaded' && (
        <ul className="mb-4 text-sm">
          {state.items.length === 0 && (
            <li className="text-[var(--text-muted)]">Nenhum cartão cadastrado.</li>
          )}
          {state.items.map((card) => (
            <li key={card.id} className="flex flex-wrap items-baseline gap-2 border-b border-[var(--border)] py-1">
              <span className="font-medium">{card.name}</span>
              <span className="text-[var(--text-muted)]">
                {card.institution} · {CARD_BRANDS[card.brand]} · {STATUSES[card.status]}
              </span>
              <span>
                fecha dia {card.closingDay}, vence dia {card.dueDay}
              </span>
              <span>
                {card.creditLimitCents === null
                  ? 'sem limite informado'
                  : `limite ${formatBRL(card.creditLimitCents)}`}
              </span>
              <button
                type="button"
                className="alvo justify-start underline"
                onClick={() => {
                  setEditingId(card.id)
                  setDraft(draftOf(card))
                  setFailure(null)
                }}
              >
                Editar {card.name}
              </button>
              {catalogo.state.status === 'loaded' && (
                <ProviderLinkField
                  kind="creditCard"
                  label={`Vínculo com a Pluggy de ${card.name}`}
                  value={card.providerAccountId}
                  options={catalogo.state.items}
                  onChange={(externalId) => {
                    void vincular(card, externalId)
                  }}
                />
              )}
              <button
                type="button"
                className="alvo justify-start underline"
                onClick={() => {
                  void setStatus(card, card.status === 'archived' ? 'active' : 'archived')
                }}
              >
                {card.status === 'archived' ? 'Reativar' : 'Arquivar'} {card.name}
              </button>
            </li>
          ))}
        </ul>
      )}

      <form
        aria-label={editingId === null ? 'Novo cartão' : 'Editar cartão'}
        onSubmit={(event) => {
          event.preventDefault()
          void submit()
        }}
      >
        {failure !== null && <Problem>{failure}</Problem>}

        <TextField
          id={`${ID}-nome`}
          label="Nome"
          value={draft.name}
          onChange={(name) => {
            change({ name })
          }}
          placeholder="Nubank Crédito"
        />
        <TextField
          id={`${ID}-instituicao`}
          label="Instituição"
          value={draft.institution}
          onChange={(institution) => {
            change({ institution })
          }}
          placeholder="Nubank"
        />
        <SelectField
          id={`${ID}-bandeira`}
          label="Bandeira"
          value={draft.brand}
          options={optionsOf(CARD_BRANDS).map((brand) => ({
            value: brand,
            label: CARD_BRANDS[brand],
          }))}
          onChange={(brand) => {
            change({ brand: brand as CreditCard['brand'] })
          }}
        />
        <TextField
          id={`${ID}-digitos`}
          label="Últimos quatro dígitos"
          value={draft.lastFourDigits}
          onChange={(lastFourDigits) => {
            change({ lastFourDigits })
          }}
          placeholder="4321"
        />
        <TextField
          id={`${ID}-limite`}
          label="Limite (em branco = não informado)"
          value={draft.creditLimit}
          onChange={(creditLimit) => {
            change({ creditLimit })
          }}
          hint={limitProblem}
          placeholder="10.000,00"
        />
        <TextField
          id={`${ID}-fechamento`}
          label="Dia do fechamento"
          value={draft.closingDay}
          onChange={(closingDay) => {
            change({ closingDay })
          }}
          hint={typeof closing === 'string' ? closing : null}
          placeholder="25"
        />
        <TextField
          id={`${ID}-vencimento`}
          label="Dia do vencimento"
          value={draft.dueDay}
          onChange={(dueDay) => {
            change({ dueDay })
          }}
          hint={typeof due === 'string' ? due : null}
          placeholder="10"
        />
        <SelectField
          id={`${ID}-conta`}
          label="Conta de pagamento (dica de conciliação)"
          value={draft.paymentAccountId}
          options={accountOptions}
          onChange={(paymentAccountId) => {
            change({ paymentAccountId })
          }}
        />

        <button type="submit" className="alvo mr-2 justify-start underline">
          {editingId === null ? 'Criar cartão' : 'Salvar cartão'}
        </button>
        {editingId !== null && (
          <button type="button" className="alvo justify-start underline" onClick={reset}>
            Cancelar edição
          </button>
        )}
      </form>
    </section>
  )
}
